import { useState, useRef, ChangeEvent, MouseEvent } from 'react';
import {
  FilenameInputProps,
  FILENAME_VARIABLES,
  FilenameVariable,
} from './types';

interface UseFilenameInputReturn {
  textFieldRef: React.RefObject<HTMLInputElement>;
  menuAnchorEl: HTMLElement | null;
  isMenuOpen: boolean;
  preview: string;
  variables: FilenameVariable[];
  handleOpenMenu: (event: MouseEvent<HTMLElement>) => void;
  handleCloseMenu: () => void;
  handleVariableClick: (variableValue: string) => void;
  handleTextFieldChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleTextFieldClick: () => void;
}

export const useFilenameInput = ({
  value,
  onChange,
  portfolios,
  reportType,
  fromDate,
  toDate,
}: FilenameInputProps): UseFilenameInputReturn => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  const isMenuOpen = Boolean(menuAnchorEl);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>): void => {
    // Store current cursor position before opening menu
    if (textFieldRef.current) {
      setCursorPosition(textFieldRef.current.selectionStart || value.length);
    }
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setMenuAnchorEl(null);
  };

  const handleVariableClick = (variableValue: string): void => {
    const newValue =
      value.slice(0, cursorPosition) +
      variableValue +
      value.slice(cursorPosition);

    onChange(newValue);
    handleCloseMenu();

    // Set focus back to text field and move cursor after inserted variable
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
        const newCursorPos = cursorPosition + variableValue.length;
        textFieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleTextFieldChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    onChange(event.target.value);
  };

  const handleTextFieldClick = (): void => {
    // Update cursor position when user clicks in the text field
    if (textFieldRef.current) {
      setCursorPosition(textFieldRef.current.selectionStart || 0);
    }
  };

  const generatePreview = (): string => {
    let preview = value;

    // Substitute {portfolio_name}
    const portfolioName =
      portfolios.length === 1
        ? portfolios[0].client_name || ''
        : portfolios.length > 1
          ? `${portfolios[0].client_name || 'Client'}`
          : '';
    preview = preview.replace(/{portfolio_name}/g, portfolioName);

    // Substitute {report_type}
    preview = preview.replace(/{report_type}/g, reportType || '');

    // Substitute {from_date}
    preview = preview.replace(/{from_date}/g, fromDate || '');

    // Substitute {to_date}
    preview = preview.replace(/{to_date}/g, toDate || '');

    // Clean up consecutive separators (underscores, hyphens, spaces)
    preview = preview.replace(/[_\-\s]+/g, '_');

    // Trim leading and trailing separators
    preview = preview.replace(/^[_\-\s]+|[_\-\s]+$/g, '');

    return preview || 'filename_template';
  };

  const preview = generatePreview();

  return {
    textFieldRef,
    menuAnchorEl,
    isMenuOpen,
    preview,
    variables: FILENAME_VARIABLES,
    handleOpenMenu,
    handleCloseMenu,
    handleVariableClick,
    handleTextFieldChange,
    handleTextFieldClick,
  };
};
